import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { prodOverview, type Gantt, type ProdDeal } from './prodpay-api';
import { CehModal } from './CehModal';
import { DealModal } from './DealModal';
import { NotificationsPanel } from './NotificationsPanel';
import { ScanModal } from './ScanModal';
import { mln } from './format';
import { useCehs } from './cehs';
import { useData } from './useData';
import { useUser } from './user-context';

const barClass = (p: number) => (p >= 80 ? 'ok' : p >= 40 ? 'warn' : 'bad');

// Канбан производства: 3 колонки по общей готовности.
const COLS: [string, (d: ProdDeal) => boolean][] = [
  ['Ожидает материалов', (d) => d.prodPct < 15],
  ['В цехах', (d) => d.prodPct >= 15 && d.prodPct < 100],
  ['Готово · ОТК', (d) => d.prodPct >= 100],
];

const pipeColor = (p: number) => (p >= 80 ? 'var(--grn)' : p >= 40 ? 'var(--gold)' : 'var(--red)');

// План-график (Гант): недели по горизонтали, полоса = окно производства, заливка = готовность.
function GanttChart({ g, onOpen }: { g: Gantt; onOpen: (id: number) => void }) {
  const { weeks, back, rows } = g;
  const today = new Date();
  const weekCols = Array.from({ length: weeks }, (_, i) => {
    const dt = new Date(today);
    dt.setDate(dt.getDate() + (i - back) * 7);
    return `${dt.getDate()}.${String(dt.getMonth() + 1).padStart(2, '0')}`;
  });
  const todayPct = (back / weeks) * 100;
  return (
    <div className="glass gantt">
      <div className="gtrow head">
        <div className="gtl">
          <b>Контракт</b>
          <span>старт · отгрузка</span>
        </div>
        <div className="gthead">
          {weekCols.map((w, i) => (
            <div className="gw" key={i}>
              {w}
            </div>
          ))}
        </div>
      </div>
      {rows.length === 0 && (
        <div className="gt-empty">
          Нет заказов в производстве. Переведите сделку на этап «Производство».
        </div>
      )}
      {rows.map((r) => {
        const s = Math.max(-back, r.start);
        const e = Math.min(weeks - back, r.ship);
        const left = ((s + back) / weeks) * 100;
        const width = Math.max(3, ((e - s) / weeks) * 100);
        const shipLeft = ((r.ship + back) / weeks) * 100;
        const shipIn = r.ship >= -back && r.ship <= weeks - back;
        return (
          <div
            className="gtrow"
            key={r.id}
            style={{ cursor: 'pointer' }}
            onClick={() => onOpen(r.id)}
          >
            <div className="gtl">
              <b>{r.client}</b>
              <span>
                {r.no}
                {r.sum != null ? ' · ' + mln(r.sum) + ' ₸' : ''}
              </span>
            </div>
            <div className="gttrack">
              <div className="gttoday" style={{ left: todayPct + '%' }} />
              <div
                className={'gtbar' + (r.warn ? ' warn' : '')}
                style={{ left: left + '%', width: width + '%' }}
                title={`${r.client} · готовность ${r.prodPct}%`}
              >
                <span className="gtf" style={{ width: r.prodPct + '%' }} />
                <span className="gtl-pct">{r.prodPct}%</span>
              </div>
              {shipIn && (
                <div className="gtdia" style={{ left: shipLeft + '%' }} title="Отгрузка" />
              )}
            </div>
          </div>
        );
      })}
      <div className="gt-legend">
        <span style={{ color: 'var(--vi)' }}>◆</span> отгрузка ·{' '}
        <span style={{ color: 'var(--red)' }}>|</span> сегодня · полоса = окно производства, заливка
        = готовность · клик — детализация
      </div>
    </div>
  );
}

export function ProdScreen() {
  const { data, refresh } = useData('prod', prodOverview);
  const [ceh, setCeh] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [scan, setScan] = useState(false);
  const nav = useNavigate();
  const user = useUser();
  const cehs = useCehs();
  const go = (screen?: string) => screen && user.screens.includes(screen) && nav('/' + screen);

  if (!data) return <p className="muted">Загрузка…</p>;

  return (
    <div className="scr">
      <div className="scr-head">
        <h2>Производство · ПДО</h2>
        <span className="muted">поток · цеха · готовность · ОТК</span>
        <button
          className="btn ghost sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => setScan(true)}
        >
          📷 Сканировать штрихкод
        </button>
      </div>

      <NotificationsPanel onOpenDeal={setOpenId} />

      <div className="section-t" style={{ marginTop: 0 }}>
        Производственный поток
      </div>
      <div className="pipe glass" style={{ padding: 12 }}>
        {data.pipeline.map((s, i) => (
          <Fragment key={i}>
            {i > 0 && <div className={'pconn' + (data.pipeline[i - 1].warn ? ' warn' : '')} />}
            <div
              className={'station' + (s.ceh || s.go ? ' clk' : '')}
              onClick={() => (s.ceh ? setCeh(s.ceh) : go(s.go))}
            >
              <div className="si">{s.ico}</div>
              <div className="sn">{s.name}</div>
              <div className="sv" style={{ color: s.warn ? 'var(--red)' : 'var(--tx)' }}>
                {s.value}
              </div>
              <div className="sb">
                <i
                  style={{
                    width: s.pct + '%',
                    background: s.warn ? 'var(--red)' : pipeColor(s.pct),
                  }}
                />
              </div>
            </div>
          </Fragment>
        ))}
      </div>

      <div className="section-t">Цеха завода</div>
      <div className="grid-cards">
        {data.cehs.map((c) => (
          <button
            key={c.id}
            className="catcard"
            style={{ textAlign: 'left', cursor: 'pointer' }}
            onClick={() => setCeh(c.id)}
          >
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
            >
              <div>
                <div className="cn">{c.name}</div>
                <div className="rmeta">{c.descr}</div>
              </div>
              <div className="mono" style={{ fontWeight: 700, fontSize: 18 }}>
                {c.avg}%
              </div>
            </div>
            <div className={'bar ' + barClass(c.avg)} style={{ marginTop: 11 }}>
              <span style={{ width: c.avg + '%' }} />
            </div>
            <div className="rmeta" style={{ marginTop: 7, color: 'var(--gold)', fontWeight: 700 }}>
              детализация →
            </div>
          </button>
        ))}
      </div>

      <div className="section-t">Заказы в производстве</div>
      <div className="kanban">
        {COLS.map(([title, f]) => {
          const cards = data.deals.filter(f);
          return (
            <div className="kcol" key={title}>
              <div className="kcol-h">
                <span>{title}</span>
                <span className="cnt">{cards.length}</span>
              </div>
              {cards.map((d) => (
                <div
                  className="kcard"
                  key={d.id}
                  style={{ cursor: 'pointer' }}
                  title="Открыть карточку · производство"
                  onClick={() => setOpenId(d.id)}
                >
                  <div className="kcli">{d.client}</div>
                  <div className="kno">
                    {d.no} · {d.units} ед.
                  </div>
                  <div style={{ marginTop: 9 }}>
                    {(['c1', 'c2', 'c3', 'c4'] as const).map((cid) => {
                      const v = d.ceh[cid] ?? 0;
                      return (
                        <div
                          key={cid}
                          style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 5 }}
                        >
                          <span className="muted" style={{ fontSize: 10.5, width: 62 }}>
                            {cehs.short(cid)}
                          </span>
                          <div
                            className={'bar ' + (v >= 100 ? 'ok' : v > 0 ? 'warn' : 'bad')}
                            style={{ flex: 1 }}
                          >
                            <span style={{ width: v + '%' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {d.otk ? (
                    <div style={{ marginTop: 9 }}>
                      <span className="chip grn">ОТК ✓</span>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div className="section-t">План-график производства</div>
      <GanttChart g={data.gantt} onOpen={setOpenId} />

      {ceh && <CehModal cehId={ceh} onClose={() => setCeh(null)} onChanged={refresh} />}
      {openId != null && (
        <DealModal
          dealId={openId}
          screens={user.screens}
          onClose={() => setOpenId(null)}
          onChanged={refresh}
          initialTab="prod"
        />
      )}
      {scan && (
        <ScanModal
          onClose={() => {
            setScan(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}
